/* eslint-disable */
import * as React from "react"
import { CircularProgress, Tooltip } from "@mui/material"
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import { SnackbarOrigin } from "@mui/material/Snackbar"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell, { tableCellClasses } from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableSortLabel from "@mui/material/TableSortLabel"
import { styled } from "@mui/material/styles"
import { visuallyHidden } from "@mui/utils"
import { connect, useSelector, type ConnectedProps } from "react-redux"
import AuthError from "../../../../components/ErrorComponents/AuthError"
import GenericModal from "../../../../components/Modal"
import Pagination from "../../../../components/TablePagination"
import LanguageStrings from "../../../../i18n/locales"
import { type RootState } from "../../../../store"
import { fetchDeviceList, deviceDeleteCampaign, getExportCustomDeviceDataService, getDeviceDataById } from "../../redux/actionCreators"
import { customDeviceListState } from "../../redux/deviceListSlice"
import { type DeviceDetailsByIdState } from "../../redux/getDeviceDataByIdSlice"
import { APIConfiguration } from '../../../../utils/apiConfiguration'
import { useNavigate } from "react-router-dom"
import EditDevice from '../EditDevice/EditDevice'
import { type exportCustomDeviceDataState } from '../../redux/exportCustomDeviceDataSlice'
import moment from 'moment-timezone'
import './DeviceTable.css'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator';
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { useAuth } from 'react-oidc-context'

const DeviceManagementStrings = LanguageStrings().CustomDeviceCollectionStrings.TableComponent;
const httpStatus = APIConfiguration

export interface State extends SnackbarOrigin {
  open: boolean;
}

interface Data {
  deviceTemplateId: string
  deviceType: string
  commTypeId: string
  commType: string
  dataTypeId: string
  datatypeName: string
  fileName: string
  uploadStatus: string
  registers: any
}
export type Order = "asc" | "desc";

interface HeadCell {
  disablePadding: boolean;
  id: any;
  label: string;
  numeric: boolean;
  sortable: boolean;
}
const headCells: readonly HeadCell[] = [
  {
    id: "deviceType",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.DeviceType,
    sortable: true,
  },
  {
    id: "commType",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.Protocol,
    sortable: true,
  },
  {
    id: "fileName",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.FileName,
    sortable: true,
  },
  {
    id: "uploadStatus",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.UploadStatus,
    sortable: true,
  },
  {
    id: "modifiedAt",
    numeric: false,
    disablePadding: false,
    label: 'Updated On',
    sortable: false,
  },
  {
    id: "action",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.Action,
    sortable: false,
  }
];

function EnhancedTableHead(props: any): any {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };
  // istanbul ignore else
  return (
    <TableHead data-testid="table-header">
      <TableRow>
        {headCells.map((headCell: any) => (
          <StyledTableCell
            key={headCell.id}
            align={
              /* istanbul ignore next */
              headCell.numeric ? "right" : "left"
            }
            padding={
              /* istanbul ignore next */
              headCell.disablePadding ? "none" : "normal"
            }
            sortDirection={
              /* istanbul ignore next */
              orderBy === headCell.id ? order : false
            }
          >
            <TableSortLabel
              sx={{
                "& .MuiTableSortLabel-icon": {
                  opacity: 1
                }
              }}
              hideSortIcon={!headCell.sortable}
              direction={
                /* istanbul ignore next */
                orderBy === headCell.id ? order : "desc"
              }
              onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
              aria-label={headCell.label}
              data-testid="test-sort"
            >
              {headCell.label}
              {
                /* istanbul ignore next */
                orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null
              }
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: "#F0F0F0",
    fontWeight: "700",
    fontSize: 12,
    border: "2px solid #202020",
    padding: "5px 16px",
    fontFamily: "Honeywell Sans Web",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#d0d0d0",
    paddingLeft: "17px",
    borderBottom: "1px solid #202020",
    borderRight: 0,
    fontFamily: "Honeywell Sans Web",
  },
}));

export interface Sort {
  order: Order;
  orderBy: string;
}

function DeviceList(props: AllProps) {
  const navigate = useNavigate()
  const [confirmDeletePopup, setConfirmDeletePopup] = React.useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = React.useState("")
  const [selectedDeviceName, setSelectedDeviceName] = React.useState("")
  const [showDeleteStatus, setShowDeleteStatus] = React.useState(false)
  const deviceDeleteStatus = useSelector((state: RootState) => state.customDeviceDeleteState);
  const [open, setOpen] = React.useState<any>(false)
  const [deviceId, setDeviceId] = React.useState("")
  const customDeviceListData = useSelector((state: RootState) => state.customDeviceList)
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()
  
  React.useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  React.useEffect(() => {
    /* istanbul ignore next */
    if (props.searchVal === "") {
      fetchAllAssetList();
    } else if (props.searchVal !== "") {
      props.performAction();
    }
  }, [props.page, props.sort]);

  const fetchAllAssetList = () => {
    // istanbul ignore else
    if (props.page >= 1) {
      props.fetchDeviceList(props.page, props.rowsPerPage, props.searchVal, props.sort.order, props.sort.orderBy);
    }
  };

  const handleRequestSort = ( event: React.MouseEvent<unknown>, newOrderBy: keyof Data ) => {
    /* istanbul ignore next */
    const isAsc = props.sort.orderBy === newOrderBy && props.sort.order === "asc";
    /* istanbul ignore next */
    const toggledOrder = isAsc ? "desc" : "asc";
    /* istanbul ignore next */
    props.setSort({ order: toggledOrder, orderBy: newOrderBy });
  };

  const formatNameWithTooltip = (text: string): JSX.Element => {
    return (<Tooltip title={text}><span>{text}</span></Tooltip>)
  }

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "& th": {
      border: "1px solid #202020",
    },
  }));

  const handleDeleteCampaignWithFailure = (): JSX.Element => {
    /* istanbul ignore next */
    if (deviceDeleteStatus.httpStatus === httpStatus.SUCCESS_HTTP_STATUS) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={DeviceManagementStrings.successfullyDeleted}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false), fetchAllAssetList()
          }}
          onClose={() => {
            setShowDeleteStatus(false), fetchAllAssetList()
          }}
        >
          <p style={{ paddingTop: "1.2em" }}>
            {" "}
            {DeviceManagementStrings.FullMsgDeviceDeletedSuccessfully}{" "}
          </p>
        </GenericModal>
      );
    } else if (deviceDeleteStatus.isLoading) {
      return (
        <GenericModal title={"Loading..."}>
          <div className="CircularprogressAmi">
            <CircularProgress />
          </div>
        </GenericModal>
      );
    } else if (deviceDeleteStatus.httpStatus === httpStatus.BAD_REQUEST) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {deviceDeleteStatus.error} </p>
        </GenericModal>
      );
    } else if (deviceDeleteStatus.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
      return (
        <div className="authError">
          <AuthError errorMessage="Unauthorized" />
        </div>
      );
    } else if (deviceDeleteStatus.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS) {
      return (
        <div className="authError">
          <AuthError errorMessage="accessForbidden" />
        </div>
      );
    } else {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {deviceDeleteStatus.error} </p>
        </GenericModal>
      );
    }
  };

  // istanbul ignore else
  if (customDeviceListData !== null && (customDeviceListData.customDeviceList?.length ?? 0) > 0 ) {
    return (
      <>
        <Box className="devices">
          <Paper
            sx={{ width: "100%", mb: 2, backgroundColor: "#272727",
              boxShadow: "unset", color: "#F0F0F0", marginBottom: "0"
            }}
          >
            <TableContainer className="device-table">
              <Table
                sx={{ minWidth: 750, color: "#f0f0f0" }}
                aria-labelledby="tableTitle" size={"small"}
              >
                <EnhancedTableHead
                  order={props.sort.order}
                  orderBy={props.sort.orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={10}
                  deviceList={customDeviceListData.customDeviceList}
                  deviceListCount={10}
                />
                <TableBody>
                  {customDeviceListData.customDeviceList &&
                    customDeviceListData.customDeviceList.map((row: any, index: any) => {
                      const labelId = `enhanced-table-checkbox-${index}`;
                      return (
                        <StyledTableRow hover role="checkbox" tabIndex={-1} key={row.id} aria-label="devices-list-row" sx={{ cursor: "pointer" }}>
                          <StyledTableCell className="column-ellipsis" component="th" id={labelId} scope="row" padding="none"
                            aria-label="device-name" data-testid="device-name"
                            sx={{ width: "18%", color: "#64C3FF !important", cursor: 'default !important' }}
                          >
                            {row.deviceType}
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: '18%', cursor: 'default !important' }}>{row.commType}</StyledTableCell>
                          <StyledTableCell sx={{ width: '18%', cursor: 'default !important' }}>{row.fileName}</StyledTableCell>
                          <StyledTableCell className="column-ellipsis" sx={{ width: '12%', cursor: 'default !important' }}>{row.uploadStatus}</StyledTableCell>
                          <StyledTableCell className="column-ellipsis" sx={{ width: '18%', cursor: 'default !important' }}>
                            {row.modifiedAt !== null && row.modifiedAt !== '' ? moment.utc(row.modifiedAt).tz(userTimezone).format('hh:mm A, MMM DD YYYY') : '-'}
                          </StyledTableCell>
                          <StyledTableCell style={{width: '280px'}}>
                            {operationPermissions.permissions.can_read_tiq_configurations && (
                              <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px", marginRight: "15px"}}
                                data-testid={'device-management-export_' + String(index)}
                                onClick={() => {
                                  props.getExportCustomDeviceDataService(row.deviceTemplateId, row.fileName)
                                }}
                              >
                                {DeviceManagementStrings.Export}
                              </span>
                            )}
                            {operationPermissions.permissions.can_modify_tiq_configurations && (
                              <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px", marginRight: "15px"}}
                                data-testid={'device-management-edit_' + String(index)}
                                onClick={() => {
                                  setDeviceId(row.deviceTemplateId)
                                  props.getDeviceDataById(row.deviceTemplateId)
                                  setOpen(true)
                                }}
                              >
                                {DeviceManagementStrings.Edit}
                              </span>
                            )}
                            {operationPermissions.permissions.can_delete_tiq_configurations && (
                              <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px", marginRight: "15px"}}
                                onClick={() => {
                                  setSelectedDeviceId(row.deviceTemplateId);
                                  setConfirmDeletePopup(true);
                                }}
                                data-testid={'device-management-delete_' + String(index)}
                              >
                                {DeviceManagementStrings.Delete}
                              </span>
                            )}
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={customDeviceListData.deviceCount}
              page={props.page}
              setPage={props.setPage}
              rowsPerPage={props.rowsPerPage}
              siblingCount={1}
              boundaryCount={1}
            />
          </Paper>
        </Box>
        {confirmDeletePopup && (
          <GenericModal
            buttonTypeNegative="hollow"
            title={`${DeviceManagementStrings.Delete} ${selectedDeviceName}`}
            LabelPositive="Confirm"
            LabelNegative="Cancel"
            onClickPositive={() => {
              setConfirmDeletePopup(false);
              setShowDeleteStatus(true);
              props.deviceDeleteCampaign(selectedDeviceId);
            }}
            onClose={() => {
              setConfirmDeletePopup(false);
            }}
            onClickNegative={() => {
              setConfirmDeletePopup(false);
            }}
          >
            <p style={{ paddingTop: "1.2em" }}>
              {DeviceManagementStrings.DeleteConfirmation}
            </p>
          </GenericModal>
        )}
        {
          showDeleteStatus && handleDeleteCampaignWithFailure()
        }
        { open &&
          <>
            <EditDevice deviceId={deviceId} isOpen={open} closePopup={(params?: any) => { setOpen(params) }} />
          </>
        }
      </>
    );
  } else if (customDeviceListData.isLoading) {
    return (
      <div className="CircularprogressAmi" data-testid="loading-spinner">
        <CircularProgress />
      </div>
    );
  } else if (customDeviceListData.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage="Unauthorized" />
      </div>
    );
  } else if (customDeviceListData.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage="accessForbidden" />
      </div>
    );
  } else if ( customDeviceListData.httpStatus === 404 && customDeviceListData.customDeviceList !== undefined) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else if (
    customDeviceListData.loadingError?.includes(DeviceManagementStrings.NoAssetsFound) ||
    customDeviceListData.loadingError?.includes(DeviceManagementStrings.AssetNotFound)
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else {
    return (
      <div className="authError">
        <AuthError errorMessage="cannotFetch" retry={fetchAllAssetList} />
      </div>
    );
  }
}

interface DispatchToProps {
  fetchDeviceList: (PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string) => void,
  deviceDeleteCampaign: (selectedDeviceId: string) => void
  getExportCustomDeviceDataService: (deviceId: string, fileName: string) => void
  getDeviceDataById: (deviceId: string) => void
  getOperationPermissions: (sub: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchDeviceList: (PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string) => dispatch(fetchDeviceList(PageNumber, PageItems, searchKey, SortParams, sortField)),
  deviceDeleteCampaign: (selectedDeviceId: string) => dispatch(deviceDeleteCampaign(selectedDeviceId)),
  getExportCustomDeviceDataService: (deviceId: string, fileName: string) => dispatch(getExportCustomDeviceDataService(deviceId, fileName)),
  getDeviceDataById: (deviceId: string) => void dispatch(getDeviceDataById(deviceId)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub))
})

interface StateToProps {
  customDeviceList: customDeviceListState
  exportCustomDeviceData: exportCustomDeviceDataState
  customDeviceListByID: DeviceDetailsByIdState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  customDeviceList: state.customDeviceList,
  exportCustomDeviceData: state.exportCustomDeviceData,
  customDeviceListByID: state.customDeviceListByID
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface ParentProps {
  searchVal: string;
  performAction: () => void;
  rowsPerPage: number;
  page: number;
  sort: Sort;
  setPage: React.Dispatch<React.SetStateAction<number>>
  setSort: React.Dispatch<React.SetStateAction<Sort>>
}

type AllProps = PropsFromRedux & ParentProps
export default connector(DeviceList)